import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  GitBranch,
  Filter,
  RefreshCw,
  Target,
  TrendingUp,
  IndianRupee,
  Users,
} from "lucide-react";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { motion } from "framer-motion";

import api from "../../common/apiClient";
import SummaryApi from "../../common/SummaryApi";
import MISFilterDrawer from "../MISFilterDrawer";


const PipelineStageAnalysisSkeleton = () => {
  return (
    <div className="bg-white rounded-2xl border p-5 animate-pulse">
      <div className="h-6 w-52 bg-gray-200 rounded mb-6" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className="h-24 bg-gray-100 rounded-xl" />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="h-[350px] bg-gray-100 rounded-xl" />

        <div className="h-[350px] bg-gray-100 rounded-xl" />
      </div>
    </div>
  );
};

const PipelineStageAnalysisError = ({ onRetry }) => {
  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 text-center">
      <h3 className="text-lg font-semibold text-red-600">
        Failed to load Pipeline Stage Analysis
      </h3>

      <button
        onClick={onRetry}
        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg"
      >
        Retry
      </button>
    </div>
  );
};

const COLORS = [
  "#DC2626",
  "#EA580C",
  "#D97706",
  "#16A34A",
  "#2563EB",
  "#7C3AED",
  "#64748B",
  "#BE123C",
];

const PipelineStageAnalysisCard = () => {
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(false);

  const [data, setData] = useState(null);

  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const [appliedFilters, setAppliedFilters] = useState({
    filterType: "thisMonth",
    divisionId: "",
    userId: "",
    stageId: "",
    industryType: "",
    fromDate: "",
    toDate: "",
  });

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);

      const params = new URLSearchParams();

      Object.entries(appliedFilters).forEach(([key, value]) => {
        if (value !== "" && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });

      const response = await api.get(
        `${SummaryApi.getPipelineStageAnalysis.url}?${params.toString()}`,
      );

      if (response?.data?.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const topStage = useMemo(() => {
    if (!data?.stageAnalysis?.length) return null;

    return [...data.stageAnalysis].sort((a, b) => b.leadCount - a.leadCount)[0];
  }, [data]);

  if (loading) {
    return <PipelineStageAnalysisSkeleton />;
  }

  if (error) {
    return <PipelineStageAnalysisError onRetry={fetchReport} />;
  }

  const summary = data?.summary || {};

  const stats = [
    {
      title: "Total Leads",
      value: summary.totalLeads || 0,
      icon: Users,
      color: "text-blue-600",
      bg: "bg-blue-50",
    },
    {
      title: "Expected Revenue",
      value: `₹${(summary.totalExpectedRevenue || 0).toLocaleString("en-IN")}`,
      icon: IndianRupee,
      color: "text-green-600",
      bg: "bg-green-50",
    },
    {
      title: "Estimated Revenue",
      value: `₹${(summary.totalEstimatedRevenue || 0).toLocaleString("en-IN")}`,
      icon: TrendingUp,
      color: "text-red-600",
      bg: "bg-red-50",
    },
    {
      title: "Pipeline Stages",
      value: data?.stageAnalysis?.length || 0,
      icon: GitBranch,
      color: "text-orange-600",
      bg: "bg-orange-50",
    },
  ];

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 20,
        }}
        whileInView={{
          opacity: 1,
          y: 0,
        }}
        transition={{
          duration: 0.4,
        }}
        viewport={{
          once: true,
        }}
        className="bg-white rounded-2xl border border-gray-200 shadow-sm"
      >
        {/* Header */}

        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-2">
            <GitBranch size={18} className="text-red-600" />

            <h2 className="font-semibold text-lg">Pipeline Stage Analysis</h2>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchReport}
              className="h-9 w-9 rounded-lg border flex items-center justify-center"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>

            <button
              onClick={() => setShowFilters(true)}
              className="h-9 w-9 rounded-lg bg-red-600 text-white flex items-center justify-center"
            >
              <Filter size={15} />
            </button>
          </div>
        </div>

        <div className="p-5">
          {/* KPI Cards */}

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
            {stats.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.title} className={`${item.bg} rounded-xl p-4`}>
                  <div className="flex justify-between">
                    <div>
                      <p className="text-xs text-gray-500">{item.title}</p>

                      <h3 className={`mt-2 text-xl font-bold ${item.color}`}>
                        {item.value}
                      </h3>
                    </div>

                    <Icon size={20} className={item.color} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Charts */}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="h-[350px] rounded-xl border p-4">
              <h3 className="font-semibold mb-4">Leads By Stage</h3>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.chart}>
                  <CartesianGrid strokeDasharray="3 3" />

                  <XAxis dataKey="stageName" />

                  <YAxis />

                  <Tooltip />

                  <Bar dataKey="count" fill="#DC2626" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="h-[350px] rounded-xl border p-4">
              <h3 className="font-semibold mb-4">Stage Distribution</h3>

              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.chart}
                    dataKey="count"
                    nameKey="stageName"
                    outerRadius={110}
                    innerRadius={60}
                  >
                    {data.chart.map((entry, index) => (
                      <Cell key={index} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>

                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Table */}

          <div className="mt-6 overflow-x-auto border rounded-xl">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3">Stage</th>

                  <th className="text-left px-4 py-3">Leads</th>

                  <th className="text-left px-4 py-3">Probability</th>

                  <th className="text-left px-4 py-3">Expected Revenue</th>

                  <th className="text-left px-4 py-3">Estimated Revenue</th>
                </tr>
              </thead>

              <tbody>
                {data.stageAnalysis.map((item) => (
                  <tr key={item.stageId} className="border-t">
                    <td className="px-4 py-3 font-medium">{item.stageName}</td>

                    <td className="px-4 py-3">{item.leadCount}</td>

                    <td className="px-4 py-3">{item.probability}%</td>

                    <td className="px-4 py-3">
                      ₹{item.expectedRevenue.toLocaleString("en-IN")}
                    </td>

                    <td className="px-4 py-3">
                      ₹{item.estimatedRevenue.toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Insight */}

          {topStage && (
            <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Target size={18} className="text-red-600" />

                <h4 className="font-semibold text-red-700">Pipeline Insight</h4>
              </div>

              <p className="text-sm text-gray-700">
                <strong>{topStage.stageName}</strong> currently holds{" "}
                <strong>{topStage.leadCount}</strong> leads and is the largest
                stage in the pipeline.
              </p>
            </div>
          )}
        </div>
      </motion.div>

      <MISFilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        filters={filters}
        setFilters={setFilters}
        onApply={() => {
          setAppliedFilters(filters);

          setShowFilters(false);
        }}
      />
    </>
  );
};

export default PipelineStageAnalysisCard;
